import React from 'react';
import { Link } from 'react-router-dom';
import type { Proposal } from '../../types';
import { Avatar } from '../ui/Avatar';
import { MaterialIcon } from '../ui/MaterialIcon';
import { formatCurrency } from '../../utils/helpers';

interface ProposalStatusChipProps {
  status: Proposal['status'];
}

// Calca ProposalStatusChip de app/obros/lib/widgets/proposal_tile.dart
const statusStyles: Record<Proposal['status'], { bg: string; fg: string; label: string }> = {
  pending: { bg: '#FEF3C7', fg: '#92400E', label: 'Pendiente' },
  accepted: { bg: '#DCFCE7', fg: '#166534', label: 'Aceptada' },
  rejected: { bg: '#FEE2E2', fg: '#991B1B', label: 'Rechazada' },
  withdrawn: { bg: '#F1EEEC', fg: '#6A5D57', label: 'Retirada' },
};

export const ProposalStatusChip: React.FC<ProposalStatusChipProps> = ({ status }) => {
  const style = statusStyles[status];
  return (
    <span
      style={{ backgroundColor: style.bg, color: style.fg }}
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
    >
      {style.label}
    </span>
  );
};

interface ProposalTileProps {
  proposal: Proposal;
  /** Para quién se muestra la tarjeta: cliente ve al worker, worker ve el job. */
  perspective: 'client' | 'worker';
}

export const ProposalTile: React.FC<ProposalTileProps> = ({ proposal, perspective }) => {
  const person = perspective === 'client' ? proposal.worker : undefined;
  const title = proposal.job?.title ?? 'Trabajo';
  const accent = statusStyles[proposal.status].fg;

  return (
    <Link
      to={`/proposals/${proposal.id}`}
      style={{ borderLeftColor: accent }}
      className="group flex gap-3 bg-surface border border-outlineVarWarm border-l-4 rounded-[18px] p-4 hover:shadow-md transition-all duration-200"
    >
      <Avatar src={person?.avatar} name={person?.full_name ?? title} size="md" ring={perspective === 'client'} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-onSurface truncate">{title}</p>
            {person && <p className="text-sm text-onSurface-mute truncate">{person.full_name}</p>}
          </div>
          <ProposalStatusChip status={proposal.status} />
        </div>

        <div className="flex items-center gap-2 mt-1.5 text-sm">
          <span className="font-semibold text-primary-700">{formatCurrency(proposal.amount)}</span>
          {proposal.estimated_days && <span className="text-onSurface-mute">· {proposal.estimated_days} días</span>}
        </div>

        {proposal.cover_letter && (
          <p className="mt-2 text-sm text-onSurface-var line-clamp-2">{proposal.cover_letter}</p>
        )}
      </div>

      <MaterialIcon name="chevron_right" size={18} className="text-onSurface-mute shrink-0 self-center group-hover:translate-x-0.5 transition-transform" />
    </Link>
  );
};
